import { Button, Dialog, SearchField, Skeleton } from "@emdur/ui";
import { useEffect, useState } from "react";
import {
  Autocomplete,
  ListBox,
  ListBoxItem,
  Text,
  useFilter,
} from "react-aria-components";
import { useNavigate } from "react-router";
import { appHref } from "../lib/clients";
import { proposalHref, proposalSentence } from "../lib/proposals";
import { useRegistry } from "./RegistryProvider";
import { agentHref } from "../lib/agents";
export function CommandPalette({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const registry = useRegistry();
  const navigate = useNavigate();
  const { contains } = useFilter({ sensitivity: "base" });
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      void registry.reload();
    }
  }, [isOpen]);
  const items = [
    {
      id: "approvals",
      label: "Open approvals",
      description: "Review changes proposed by agents",
      href: "/approvals",
    },
    ...registry.organisations.flatMap((org) => [
      {
        id: org.organization_id,
        label: org.organization_name,
        description: "Organisation",
        href: `/orgs/${org.organization_id}`,
      },
      {
        id: `register-${org.organization_id}`,
        label: "Register an app",
        description: org.organization_name,
        href: `${appHref(org.organization_id)}?register`,
      },
      {
        id: `agents-${org.organization_id}`,
        label: "Agents",
        description: org.organization_name,
        href: agentHref(org.organization_id),
      },
      {
        id: `create-agent-${org.organization_id}`,
        label: "Create an agent",
        description: org.organization_name,
        href: `${agentHref(org.organization_id)}?create`,
      },
      {
        id: `roles-${org.organization_id}`,
        label: "Roles",
        description: org.organization_name,
        href: `/orgs/${org.organization_id}/roles`,
      },
    ]),
    ...registry.apps.map((app) => ({
      id: app.id,
      label: app.client_name,
      description: "App",
      href: appHref(app.organization_id, app.id),
    })),
    ...Object.values(registry.agents).map((agent) => ({
      id: agent.id,
      label: agent.name,
      description: "Agent",
      href: agentHref(agent.organization_id, agent.id),
    })),
    ...registry.proposals.map((proposal) => ({
      id: proposal.id,
      label: proposalSentence(
        proposal,
        registry.catalogues[proposal.organization_id] ?? [],
        registry.apps.find(
          (app) => app.id === proposal.arguments.path?.client_id,
        )?.client_name,
      ),
      description: `Proposal · ${registry.agents[proposal.agent_id]?.name ?? "Agent"}`,
      href: proposalHref(proposal.organization_id, proposal.id),
    })),
  ];
  return (
    <Dialog title="Go to…" isOpen={isOpen} onOpenChange={onOpenChange}>
      <div
        onKeyDownCapture={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            onOpenChange(false);
          }
        }}
      >
        <Autocomplete
          filter={contains}
          inputValue={query}
          onInputChange={setQuery}
        >
          <SearchField
            label="Find an organisation, app, agent or proposal"
            autoFocus
          />
          {registry.loading && <Skeleton label="Loading destinations" />}
          <ListBox
            aria-label="Destinations"
            items={items}
            onAction={(key) => {
              const item = items.find((item) => item.id === key);
              if (item) {
                onOpenChange(false);
                void navigate(item.href);
              }
            }}
            renderEmptyState={() => (
              <p className="p-4 text-sm text-muted">
                No matches. Try the app or organisation's name.
              </p>
            )}
            className="mt-3 max-h-80 overflow-y-auto outline-none"
          >
            {(item) => (
              <ListBoxItem
                id={item.id}
                textValue={`${item.label} ${item.description}`}
                className="flex cursor-default flex-col rounded-control px-3 py-3 text-ink data-[focused]:bg-action data-[focused]:text-on-action"
              >
                <Text slot="label">
                  <span>{item.label}</span>{" "}
                  <span className="block text-sm">{item.description}</span>
                </Text>
              </ListBoxItem>
            )}
          </ListBox>
        </Autocomplete>
      </div>
      <div className="mt-3">
        <Button onPress={() => onOpenChange(false)}>Close</Button>
      </div>
    </Dialog>
  );
}
